// GET /academy/setup only reports current state, so "just completed" is
// derived by diffing the previously fetched items against the new ones.
import {
  AcademySetupItem,
  AcademySetupItemKey,
  AcademySetupOptionalStates,
  AcademySetupPhaseKey,
  academySetupPhases,
} from "./items";

/**
 * `states` without the marks whose PUT has not confirmed yet. An optimistic
 * mark completes its row at once, but a write that then fails must not have
 * counted toward an item or phase event — those fire on confirmation.
 */
export const settledOptionalStates = (
  states: AcademySetupOptionalStates,
  pending: Partial<Record<AcademySetupItemKey, true>>,
): AcademySetupOptionalStates => {
  const settled: AcademySetupOptionalStates = { ...states };
  (Object.keys(pending) as AcademySetupItemKey[]).forEach((key) => {
    delete settled[key];
  });
  return settled;
};

export interface CompletionTransitions {
  /** Items whose API flag flipped false → true. */
  completedKeys: AcademySetupItemKey[];
  /** Phases whose every item became complete (flag, visit or skip) on this transition. */
  completedPhases: AcademySetupPhaseKey[];
}

const phaseComplete = (
  items: AcademySetupItem[],
  phase: AcademySetupPhaseKey,
): boolean => {
  return items
    .filter((item) => item.phase === phase)
    .every((item) => item.complete);
};

/** `prev` null (nothing fetched yet) fires nothing: arriving already complete is not completing. */
export const completionTransitions = (
  prev: AcademySetupItem[] | null,
  next: AcademySetupItem[],
): CompletionTransitions => {
  if (!prev) {
    return { completedKeys: [], completedPhases: [] };
  }

  const wasDone = new Set(
    prev.filter((item) => item.done).map((item) => item.key),
  );
  const completedKeys = next
    .filter((item) => item.done && !wasDone.has(item.key))
    .map((item) => item.key);

  const completedPhases = academySetupPhases
    .map((phase) => phase.key)
    .filter(
      (phase) => phaseComplete(next, phase) && !phaseComplete(prev, phase),
    );

  return { completedKeys, completedPhases };
};

/**
 * Items that became complete for progress (flag, visit or skip) on this
 * transition — what the per-source completion tally attributes. A flag that
 * lands on an already visited row is not reported again: the CTA that first
 * completed the row keeps the credit. `prev` null (nothing fetched yet)
 * reports nothing, as above.
 */
export const newlyCompleteKeys = (
  prev: AcademySetupItem[] | null,
  next: AcademySetupItem[],
): AcademySetupItemKey[] => {
  if (!prev) {
    return [];
  }
  const wasComplete = new Set(
    prev.filter((item) => item.complete).map((item) => item.key),
  );
  return next
    .filter((item) => item.complete && !wasComplete.has(item.key))
    .map((item) => item.key);
};
